import React, { useEffect, useState } from 'react';
import { useTranslation } from '../contexts/LanguageContext';
import { useApi } from '../hooks/useApi';
import { useToast } from '../components/Shared/Toast';

/**
 * ProfilePage - eigenes Profil, Einstellungen, Passwort.
 * Backend (bestehend): GET/PUT /api/platform/auth/profile, PUT /api/platform/auth/change-password.
 */

const ROLE_LABELS = {
  platform_admin: 'Platform-Admin',
  org_admin: 'Org-Admin',
  user: 'Nutzer',
};

const section = {
  background: 'var(--bg-card)', border: '1px solid var(--border)',
  borderRadius: 10, padding: 20, marginBottom: 16,
};

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
      <div style={{ width: 160, fontSize: 12, color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

export default function ProfilePage() {
  const { t } = useTranslation();
  const { api } = useApi();
  const { showToast } = useToast();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState('');
  const [language, setLanguage] = useState('');
  const [theme, setTheme] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    let alive = true;
    api('auth/profile')
      .then(d => {
        if (!alive) return;
        setProfile(d);
        setDisplayName(d.display_name || '');
        setLanguage((d.profile && d.profile.language) || '');
        setTheme((d.profile && d.profile.theme) || '');
      })
      .catch(err => showToast(t('common.error') + ': ' + (err?.message || ''), 'error'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [api, showToast, t]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const body = { display_name: displayName };
      if (language) body.language = language;
      if (theme) body.theme = theme;
      await api('auth/profile', { method: 'PUT', body });
      showToast(t('profile.saved', 'Profil gespeichert'), 'success');
    } catch (err) {
      showToast(t('common.error') + ': ' + (err?.message || ''), 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async () => {
    if (newPw.length < 8) {
      showToast(t('profile.pw_too_short', 'Neues Passwort: mindestens 8 Zeichen'), 'error');
      return;
    }
    if (newPw !== newPw2) {
      showToast(t('profile.pw_mismatch', 'Passwoerter stimmen nicht ueberein'), 'error');
      return;
    }
    setSavingPw(true);
    try {
      await api('auth/change-password', {
        method: 'PUT', body: { current_password: curPw, new_password: newPw },
      });
      showToast(t('profile.pw_changed', 'Passwort geaendert'), 'success');
      setCurPw(''); setNewPw(''); setNewPw2('');
    } catch (err) {
      showToast(t('common.error') + ': ' + (err?.response?.data?.detail || err?.message || ''), 'error');
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <div className="page-title">{t('profile.title')}</div>
        <div className="page-subtitle">{t('profile.subtitle')}</div>
      </div>
      <div className="page-content">
        {loading ? (
          <div style={{ ...section, textAlign: 'center', color: 'var(--text-muted)' }}>
            {t('common.loading', 'Lade…')}
          </div>
        ) : !profile ? (
          <div style={{ ...section, textAlign: 'center', color: 'var(--text-muted)' }}>
            {t('common.error', 'Fehler')}
          </div>
        ) : (
          <>
            {/* Stammdaten / Einstellungen */}
            <div style={section}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>
                {t('profile.account', 'Konto & Einstellungen')}
              </div>
              <Row label={t('profile.email', 'E-Mail')}>
                <span style={{ fontSize: 13 }}>{profile.email}</span>
              </Row>
              <Row label={t('profile.role', 'Rolle')}>
                <span style={{ fontSize: 13 }}>{ROLE_LABELS[profile.platform_role] || profile.platform_role}</span>
              </Row>
              <Row label={t('profile.tenant', 'Mandant')}>
                <span style={{ fontSize: 13 }}>
                  {profile.tenant_name || '—'}{profile.tenant_type ? ` (${profile.tenant_type})` : ''}
                </span>
              </Row>
              <Row label={t('profile.mfa', 'MFA')}>
                <span style={{ fontSize: 13 }}>
                  {profile.mfa_enabled ? t('profile.mfa_on', 'aktiv') : t('profile.mfa_off', 'nicht aktiv')}
                </span>
              </Row>
              <div style={{ height: 1, background: 'var(--border)', margin: '12px 0' }} />
              <Row label={t('profile.display_name', 'Anzeigename')}>
                <input className="field-input" value={displayName}
                  onChange={e => setDisplayName(e.target.value)} style={{ maxWidth: 320 }} />
              </Row>
              <Row label={t('profile.language', 'Sprache')}>
                <select className="field-input" value={language}
                  onChange={e => setLanguage(e.target.value)} style={{ maxWidth: 200 }}>
                  <option value="">—</option>
                  <option value="de">Deutsch</option>
                  <option value="en">English</option>
                </select>
              </Row>
              <Row label={t('profile.theme', 'Darstellung')}>
                <select className="field-input" value={theme}
                  onChange={e => setTheme(e.target.value)} style={{ maxWidth: 200 }}>
                  <option value="">—</option>
                  <option value="dark">Dark</option>
                  <option value="light">Light</option>
                </select>
              </Row>
              <button className="btn btn-primary" disabled={savingProfile} onClick={saveProfile}>
                {savingProfile ? t('profile.saving', 'Speichere…') : t('profile.save', 'Speichern')}
              </button>
            </div>

            {/* Passwort */}
            <div style={section}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
                {t('profile.password', 'Passwort aendern')}
              </div>
              {profile.profile && profile.profile.sso_enabled && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                  {t('profile.sso_hint', 'SSO ist aktiv - Passwortaenderung ggf. ueber den Identity-Provider.')}
                </div>
              )}
              <Row label={t('profile.current_pw', 'Aktuelles Passwort')}>
                <input type="password" className="field-input" value={curPw}
                  onChange={e => setCurPw(e.target.value)} autoComplete="current-password" style={{ maxWidth: 320 }} />
              </Row>
              <Row label={t('profile.new_pw', 'Neues Passwort')}>
                <input type="password" className="field-input" value={newPw}
                  onChange={e => setNewPw(e.target.value)} autoComplete="new-password" style={{ maxWidth: 320 }} />
              </Row>
              <Row label={t('profile.new_pw2', 'Wiederholen')}>
                <input type="password" className="field-input" value={newPw2}
                  onChange={e => setNewPw2(e.target.value)} autoComplete="new-password" style={{ maxWidth: 320 }} />
              </Row>
              <button className="btn btn-primary" disabled={savingPw || !curPw || !newPw}
                onClick={changePassword}>
                {savingPw ? t('profile.saving', 'Speichere…') : t('profile.change_pw', 'Passwort aendern')}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
